import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/authStore";
import { useQuota } from "@/hooks/useQuota";
import { useBugReport } from "@/hooks/useBugReport";
import { BugReportModal } from "@/components/BugReportModal";
import { changeLanguage, type SupportedLanguage } from "@/lib/i18n";

const languages: { code: SupportedLanguage; label: string }[] = [
  { code: "fr", label: "Francais" },
  { code: "en", label: "English" },
  { code: "es", label: "Espanol" },
];

export function Settings() {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuthStore();
  const { quota } = useQuota();
  const bugReport = useBugReport();

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    await changeLanguage(lang);
  };

  return (
    <div className="max-w-lg mx-auto">
      <h2 className="text-xl font-bold text-redi-text mb-6">
        {t("tabs.settings")}
      </h2>

      {/* Account */}
      <div className="bg-redi-surface rounded-xl p-5 mb-4">
        <h3 className="text-sm font-semibold text-redi-text mb-3">
          {t("settings.account")}
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-redi-muted">Email</span>
            <span className="text-sm text-redi-text">{user?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-redi-muted">{t("settings.subscription")}</span>
            <span className="text-sm text-redi-text capitalize">
              {quota?.plan ?? "free"}
            </span>
          </div>
          {quota?.plan === "free" && (
            <div className="flex justify-between">
              <span className="text-sm text-redi-muted">
                {t("settings.usageThisMonth")}
              </span>
              <span className="text-sm text-redi-text">
                {quota?.meetings_this_month ?? 0} / 3
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade */}
      {quota?.plan === "free" && (
        <a
          href="/pricing"
          className="block bg-redi-primary/10 border border-redi-primary/20 rounded-xl p-4 mb-4 text-center hover:bg-redi-primary/15 transition-colors"
        >
          <p className="text-redi-primary font-semibold text-sm">
            {t("settings.upgradeTitle")}
          </p>
        </a>
      )}

      {/* Language */}
      <div className="bg-redi-surface rounded-xl p-5 mb-4">
        <h3 className="text-sm font-semibold text-redi-text mb-3">
          {t("settings.language")}
        </h3>
        <div className="flex gap-2">
          {languages.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                i18n.language === code
                  ? "bg-redi-primary text-white"
                  : "bg-redi-dark text-redi-muted hover:text-redi-text"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* About & Bug report */}
      <div className="bg-redi-surface rounded-xl p-5 mb-4 space-y-3">
        <button
          onClick={bugReport.open}
          className="flex items-center gap-2 text-sm text-redi-muted hover:text-redi-text transition-colors w-full"
        >
          <span>&#128027;</span>
          <span>{t("bugReport.menuLabel")}</span>
        </button>
        <div className="flex justify-between">
          <span className="text-sm text-redi-muted">{t("settings.version")}</span>
          <span className="text-sm text-redi-text">1.0.0 (web)</span>
        </div>
      </div>

      {/* Sign out */}
      <button
        onClick={signOut}
        className="w-full py-3 text-redi-error text-sm font-medium rounded-xl border border-redi-error/20 hover:bg-redi-error/5 transition-colors"
      >
        {t("settings.signOut")}
      </button>

      <BugReportModal
        isOpen={bugReport.isOpen}
        isSubmitting={bugReport.isSubmitting}
        screenshotUrl={bugReport.screenshotUrl}
        success={bugReport.success}
        error={bugReport.error}
        onSubmit={bugReport.submit}
        onClose={bugReport.close}
      />
    </div>
  );
}
